import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser audio/video recording via MediaRecorder. Ported and generalised from the Booka
 * voice-capture work (lib/voice/use-whisper-recording.ts): this version is transcription-agnostic
 * - it only records and hands back a Blob. Upload + transcription are the caller's job
 * (see check-in-recorder.tsx).
 */

export type MediaRecordingKind = 'audio' | 'video';

export type MediaRecordingErrorKind =
  | 'unsupported'
  | 'not-allowed'
  | 'no-device'
  | 'unknown';

export interface MediaRecordingResult {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
  kind: MediaRecordingKind;
}

export interface UseMediaRecordingOptions {
  kind: MediaRecordingKind;
  /** Auto-stop after this many seconds. Default 300 (5 min). */
  maxSeconds?: number;
  /** Called with the finished recording when the recorder stops. */
  onComplete?: (result: MediaRecordingResult) => void;
}

export interface UseMediaRecordingResult {
  recording: boolean;
  elapsedSeconds: number;
  unsupported: boolean;
  errorMessage: string | null;
  /** Live MediaStream while recording - attach to a muted <video> for a video preview. Null otherwise. */
  previewStream: MediaStream | null;
  start: () => void;
  stop: () => void;
  /** Tear down the stream/recorder without emitting a result (e.g. when discarding a take). */
  cancel: () => void;
}

const ERROR_MESSAGES: Record<MediaRecordingErrorKind, string> = {
  unsupported: 'Recording is not supported in this browser. Try Chrome, Edge, or Firefox.',
  'not-allowed': 'Permission was denied. Allow microphone/camera access to record.',
  'no-device': 'No microphone or camera was found.',
  unknown: 'Something went wrong while recording. Please try again.',
};

const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

const VIDEO_MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4',
];

function pickRecorderMimeType(kind: MediaRecordingKind): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const candidates = kind === 'video' ? VIDEO_MIME_CANDIDATES : AUDIO_MIME_CANDIDATES;
  for (const c of candidates) {
    if (MediaRecorder.isTypeSupported(c)) return c;
  }
  return undefined;
}

function canRecord(): boolean {
  if (typeof window === 'undefined') return false;
  const getUserMedia = navigator.mediaDevices?.getUserMedia;
  return typeof getUserMedia === 'function' && typeof MediaRecorder !== 'undefined';
}

export function useMediaRecording(options: UseMediaRecordingOptions): UseMediaRecordingResult {
  const { kind, maxSeconds = 300, onComplete } = options;

  const [recording, setRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [error, setError] = useState<MediaRecordingErrorKind | null>(null);
  const [unsupported, setUnsupported] = useState(false);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeTypeRef = useRef<string | undefined>(undefined);
  const startedAtRef = useRef<number>(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onCompleteRef = useRef(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    setUnsupported(!canRecord());
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const cleanupStream = useCallback(() => {
    const stream = streamRef.current;
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    recorderRef.current = null;
    chunksRef.current = [];
    setPreviewStream(null);
    clearTimer();
  }, [clearTimer]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    clearTimer();
    if (!recorder || recorder.state === 'inactive') {
      cleanupStream();
      setRecording(false);
      return;
    }
    const durationSeconds = Math.max(
      1,
      Math.round((Date.now() - startedAtRef.current) / 1000),
    );
    recorder.onstop = () => {
      const mime = mimeTypeRef.current || (kind === 'video' ? 'video/webm' : 'audio/webm');
      const blob = new Blob(chunksRef.current, { type: mime });
      cleanupStream();
      setRecording(false);
      if (blob.size > 0) {
        onCompleteRef.current?.({ blob, mimeType: mime, durationSeconds, kind });
      } else {
        setError('unknown');
      }
    };
    try {
      if (recorder.state === 'recording') {
        try {
          recorder.requestData();
        } catch {
          // optional
        }
      }
      recorder.stop();
    } catch {
      cleanupStream();
      setRecording(false);
      setError('unknown');
    }
  }, [cleanupStream, clearTimer, kind]);

  const start = useCallback(async () => {
    if (unsupported) {
      setError('unsupported');
      return;
    }
    if (recorderRef.current?.state === 'recording') return;

    setError(null);
    setElapsedSeconds(0);
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(
        kind === 'video' ? { audio: true, video: true } : { audio: true },
      );
    } catch (e) {
      const name = (e as DOMException)?.name;
      setError(name === 'NotFoundError' || name === 'DevicesNotFoundError' ? 'no-device' : 'not-allowed');
      return;
    }

    const mimeType = pickRecorderMimeType(kind);
    mimeTypeRef.current = mimeType;

    let recorder: MediaRecorder;
    try {
      recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      setError('unsupported');
      return;
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    if (kind === 'video') setPreviewStream(stream);

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    try {
      recorder.start(250);
      startedAtRef.current = Date.now();
      setRecording(true);
      clearTimer();
      timerRef.current = setInterval(() => {
        const secs = Math.round((Date.now() - startedAtRef.current) / 1000);
        setElapsedSeconds(secs);
        if (secs >= maxSeconds) stop();
      }, 250);
    } catch {
      cleanupStream();
      setError('unknown');
    }
  }, [unsupported, kind, maxSeconds, clearTimer, cleanupStream, stop]);

  const cancel = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.onstop = null as unknown as MediaRecorder['onstop'];
      try {
        recorder.stop();
      } catch {
        // ignore
      }
    }
    cleanupStream();
    setRecording(false);
    setElapsedSeconds(0);
    setError(null);
  }, [cleanupStream]);

  useEffect(() => {
    return () => {
      const r = recorderRef.current;
      if (r && r.state !== 'inactive') {
        r.onstop = null as unknown as MediaRecorder['onstop'];
        try {
          r.stop();
        } catch {
          // ignore
        }
      }
      cleanupStream();
    };
  }, [cleanupStream]);

  const errorMessage = error ? ERROR_MESSAGES[error] : null;

  return {
    recording,
    elapsedSeconds,
    unsupported,
    errorMessage,
    previewStream,
    start: () => void start(),
    stop,
    cancel,
  };
}
